import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth-context';
import { usePassCodeur } from '../../lib/pass-codeur-context';
import { getCourseByIdStatic } from '../../pages/cours/courses-data';

// Import des composants de cours génériques
import { CourseLayout } from './CourseLayout';
import { CourseNavigation } from './CourseNavigation';
import { Button } from '../ui/button';
import { InfoBox, PatternContainer, Separator } from './CourseComponents';
import { Crown, LogIn } from 'lucide-react';
import SEO from '../SEO';
import Breadcrumbs, { generateCourseBreadcrumbs } from '../ui/breadcrumbs';

// Fonction utilitaire pour trouver le chapitre suivant
const getNextChapter = (courseStructure, currentChapterId) => {
  const currentIndex = courseStructure.chapters.findIndex(
    chapter => chapter.id === currentChapterId
  );
  
  if (currentIndex >= 0 && currentIndex < courseStructure.chapters.length - 1) {
    return courseStructure.chapters[currentIndex + 1];
  }
  
  return null;
};

// Fonction utilitaire pour trouver le chapitre précédent
const getPreviousChapter = (courseStructure, currentChapterId) => {
  const currentIndex = courseStructure.chapters.findIndex(
    chapter => chapter.id === currentChapterId
  );
  
  if (currentIndex > 0) {
    return courseStructure.chapters[currentIndex - 1];
  }
  
  return null;
};

const GenericCourseIntro = ({ 
  courseStructure,
  IntroductionContent
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { hasAccessToCourses } = usePassCodeur();
  const [courseInfo, setCourseInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Validation de courseStructure
  if (!courseStructure) {
    console.error('GenericCourseIntro: courseStructure est undefined');
    return <div>Erreur: Structure de cours manquante</div>;
  }
  
  if (!courseStructure.chapters) {
    console.error('GenericCourseIntro: courseStructure.chapters est undefined', courseStructure);
    return <div>Erreur: Chapitres du cours manquants</div>;
  }
  
  // Récupérer l'ID du cours depuis la structure
  const courseId = courseStructure.id;

  useEffect(() => {
    const fetchCourseData = () => {
      try {
        setIsLoading(true);
        // Récupérer les infos du cours depuis les données statiques
        const course = getCourseByIdStatic(courseId);
        setCourseInfo(course);
      } catch (error) {
        console.error('Erreur lors de la récupération des données du cours:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCourseData();
  }, [courseId]);

  // Scroll vers le haut quand on arrive sur la page d'introduction
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fonction pour vérifier l'accès au cours
  const hasAccessToCourse = () => {
    if (!courseInfo) return false;
    return courseInfo.isFree || hasAccessToCourses();
  };

  // Note: On ne redirige plus les utilisateurs non connectés vers login
  // Ils peuvent voir l'introduction comme un utilisateur sans Pass Codeur

  // Fonctions de navigation avec courseStructure pré-bindé (avec validation)
  const curriedNextChapter = (chapterId) => {
    if (!courseStructure || !courseStructure.chapters) {
      console.warn('courseStructure ou courseStructure.chapters est undefined');
      return null;
    }
    return getNextChapter(courseStructure, chapterId);
  };
  
  const curriedPreviousChapter = (chapterId) => {
    if (!courseStructure || !courseStructure.chapters) {
      console.warn('courseStructure ou courseStructure.chapters est undefined');
      return null;
    }
    return getPreviousChapter(courseStructure, chapterId);
  };

  return (
    <>
      <SEO 
        title={`${courseStructure.title} - Formation programmation de jeux vidéo`}
        description={`${courseStructure.description} Apprenez à programmer ${courseStructure.title} étape par étape avec notre formation interactive.`}
        keywords={`${courseInfo?.seoKeywords || courseStructure.title}, programmation, jeu vidéo, p5.js, JavaScript, formation, cours`}
        url={`/cours/${courseId}`}
        type="article"
        image={courseStructure.image}
      />
      <CourseLayout 
        courseStructure={courseStructure} 
        courseId={courseId}
        isPurchased={hasAccessToCourse()} // Vérifie l'accès au cours (Pass Codeur ou cours gratuit)
      >
      {/* Breadcrumbs */}
      <Breadcrumbs items={generateCourseBreadcrumbs(courseInfo)} />
      
      <div className="mb-8">
        <div className="mb-8 max-w-full">
          <img 
            src={courseStructure.image} 
            alt={courseStructure.title}
            className="w-full h-56 md:h-64 object-cover rounded-lg shadow-md"
          />
          <div className="mt-6 mb-2 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">{courseStructure.title}</h1>
              <div className="text-muted-foreground mt-2">Introduction</div>
            </div>
            {!currentUser && (
              <Button 
                onClick={() => navigate(`/login?redirect=${encodeURIComponent(`/cours/${courseId}`)}`)}
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shrink-0"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Créer un compte / Se connecter
              </Button>
            )}
          </div>
          <div className="mb-8"></div>
          
          <Separator type="line" color="gray-dark" size="md" />
        </div>
        
        {/* Contenu de l'introduction */}
        <div className="prose prose-lg max-w-none space-y-6">
          <IntroductionContent />

          {/* Note: Plus besoin de section spéciale pour les utilisateurs non connectés */}
          {/* Ils peuvent voir l'introduction et ont accès au bouton Pass Codeur en bas */}
        </div>
        
        {/* Navigation - affichée pour tous les utilisateurs */}
        {courseStructure && courseStructure.chapters && (
          <div className="mt-12 pt-6">
            <Separator type="line" color="gray-dark" noPaddingTop />
            {currentUser && hasAccessToCourse() ? (
              <CourseNavigation
                courseId={courseId}
                chapterId='introduction'
                getNextChapter={curriedNextChapter}
                getPreviousChapter={curriedPreviousChapter}
              />
            ) : !currentUser ? (
              <div className="flex justify-center">
                <Button 
                  onClick={() => navigate(`/login?redirect=${encodeURIComponent(`/cours/${courseId}`)}`)}
                  className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-lg px-8 py-3"
                >
                  <LogIn className="h-5 w-5 mr-2" />
                  Créer un compte gratuit pour continuer
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </CourseLayout>
    </>
  );
};

export default GenericCourseIntro; 